/**
 * Abfragen und Änderungen über TanStack Query.
 *
 * Der Prüfbogen ist der Sonderfall: er wird zusätzlich in IndexedDB abgelegt
 * und aus dort gelesen, wenn keine Verbindung besteht. Alles andere braucht
 * das nicht – Stammdaten pflegt man im Büro.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './klient'
import { bogenAblegen, bogenLesen } from '../offline/db'
import type {
  Bausteinkategorie,
  Mangelstatus,
  Foto,
  Geraet,
  Geraetetyp,
  Kunde,
  Kundenartkatalog,
  Platzkriterium,
  Pruefbogen,
  Pruefstatus,
  Pruefung,
  Spielplatz,
  Stufe,
  Textbaustein,
} from './typen'

export const schluessel = {
  kunden: ['kunden'] as const,
  kunde: (id: string) => ['kunde', id] as const,
  spielplaetze: (kundeId?: string) => ['spielplaetze', kundeId ?? 'alle'] as const,
  spielplatz: (id: string) => ['spielplatz', id] as const,
  geraete: (platzId: string) => ['geraete', platzId] as const,
  pruefungen: (filter?: string) => ['pruefungen', filter ?? 'alle'] as const,
  bogen: (id: string) => ['bogen', id] as const,
  fotos: (pruefungId: string) => ['fotos', pruefungId] as const,
  platzkriterien: ['platzkriterien'] as const,
  geraetetypen: ['geraetetypen'] as const,
  bausteine: (kategorie?: Bausteinkategorie) => ['bausteine', kategorie ?? 'alle'] as const,
}

// ── Stammdaten ───────────────────────────────────────────────────

export function useKunden() {
  return useQuery({
    queryKey: schluessel.kunden,
    queryFn: ({ signal }) => api.holen<Kunde[]>('/api/kunden', signal),
  })
}

export function useSpielplaetze(kundeId?: string) {
  return useQuery({
    queryKey: schluessel.spielplaetze(kundeId),
    queryFn: ({ signal }) =>
      api.holen<Spielplatz[]>(
        kundeId ? `/api/spielplaetze?kunde_id=${kundeId}` : '/api/spielplaetze',
        signal,
      ),
  })
}

export function useSpielplatz(id: string) {
  return useQuery({
    queryKey: schluessel.spielplatz(id),
    queryFn: ({ signal }) => api.holen<Spielplatz>(`/api/spielplaetze/${id}`, signal),
  })
}

export function useGeraete(platzId: string) {
  return useQuery({
    queryKey: schluessel.geraete(platzId),
    queryFn: ({ signal }) =>
      api.holen<Geraet[]>(`/api/spielplaetze/${platzId}/geraete`, signal),
  })
}

export function useKundeAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: Partial<Kunde>) => api.senden<Kunde>('/api/kunden', daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: schluessel.kunden }),
  })
}

export function useSpielplatzAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: Partial<Spielplatz> & { kunde_id: string }) =>
      api.senden<Spielplatz>('/api/spielplaetze', daten),
    onSuccess: (angelegt) => {
      speicher.invalidateQueries({ queryKey: ['spielplaetze'] })
      speicher.invalidateQueries({ queryKey: schluessel.kunde(angelegt.kunde_id) })
      speicher.invalidateQueries({ queryKey: schluessel.kunden })
    },
  })
}

export function useGeraetAnlegen(platzId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: Partial<Geraet>) =>
      api.senden<Geraet>(`/api/spielplaetze/${platzId}/geraete`, daten),
    onSuccess: () => {
      speicher.invalidateQueries({ queryKey: schluessel.geraete(platzId) })
      speicher.invalidateQueries({ queryKey: ['spielplaetze'] })
    },
  })
}

export function useKundeAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: Partial<Kunde> & { id: string }) =>
      api.aendern<Kunde>(`/api/kunden/${id}`, daten),
    onSuccess: (kunde) => {
      speicher.invalidateQueries({ queryKey: schluessel.kunde(kunde.id) })
      speicher.invalidateQueries({ queryKey: schluessel.kunden })
      // Der Kundenname steht auch an jedem Spielplatz.
      speicher.invalidateQueries({ queryKey: ['spielplaetze'] })
    },
  })
}

export function useSpielplatzAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: Partial<Spielplatz> & { id: string }) =>
      api.aendern<Spielplatz>(`/api/spielplaetze/${id}`, daten),
    onSuccess: (platz) => {
      speicher.invalidateQueries({ queryKey: schluessel.spielplatz(platz.id) })
      speicher.invalidateQueries({ queryKey: ['spielplaetze'] })
      speicher.invalidateQueries({ queryKey: schluessel.kunde(platz.kunde_id) })
    },
  })
}

export function useGeraetAendern(platzId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: Partial<Geraet> & { id: string }) =>
      api.aendern<Geraet>(`/api/geraete/${id}`, daten),
    onSuccess: () => {
      speicher.invalidateQueries({ queryKey: schluessel.geraete(platzId) })
      speicher.invalidateQueries({ queryKey: ['spielplaetze'] })
    },
  })
}

export function useGeraetAusmustern(platzId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, am }: { id: string; am: string }) =>
      api.senden<Geraet>(`/api/geraete/${id}/ausmustern`, { ausgemustert_am: am }),
    onSuccess: () => {
      speicher.invalidateQueries({ queryKey: schluessel.geraete(platzId) })
      speicher.invalidateQueries({ queryKey: ['spielplaetze'] })
    },
  })
}

// ── Kataloge ─────────────────────────────────────────────────────

export function usePlatzkriterien() {
  return useQuery({
    queryKey: schluessel.platzkriterien,
    queryFn: ({ signal }) =>
      api.holen<Platzkriterium[]>('/api/kataloge/platzkriterien', signal),
    // Kataloge ändern sich selten; eine Stunde reicht.
    staleTime: 60 * 60 * 1000,
  })
}

/* Auch die stillgelegten – die Katalogpflege muss sie sehen, um sie wieder
   in Betrieb nehmen zu können. Der Prüfbogen fragt ohne diesen Schalter. */
export function useAllePlatzkriterien() {
  return useQuery({
    queryKey: [...schluessel.platzkriterien, 'alle'] as const,
    queryFn: ({ signal }) =>
      api.holen<Platzkriterium[]>(
        '/api/kataloge/platzkriterien?nur_aktive=false',
        signal,
      ),
  })
}

function kriterienNeuLaden(speicher: ReturnType<typeof useQueryClient>) {
  return speicher.invalidateQueries({ queryKey: schluessel.platzkriterien })
}

export function usePlatzkriteriumAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: {
      gruppe: string
      bezeichnung: string
      hinweis?: string | null
      normverweis?: string | null
    }) => api.senden<Platzkriterium>('/api/kataloge/platzkriterien', daten),
    onSuccess: () => kriterienNeuLaden(speicher),
  })
}

export function usePlatzkriteriumAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Partial<Platzkriterium>) =>
      api.aendern<Platzkriterium>(`/api/kataloge/platzkriterien/${id}`, daten),
    onSuccess: () => kriterienNeuLaden(speicher),
  })
}

/** Einen Prüfpunkt wirklich löschen.
 *
 *  Geht nur, solange keine Prüfung ihn beurteilt hat – der Server weist es
 *  sonst ab und nennt die Zahl. Für alles andere ist Stilllegen der Weg. */
export function usePlatzkriteriumLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/kataloge/platzkriterien/${id}`),
    onSuccess: () => kriterienNeuLaden(speicher),
  })
}

// ── Kundenarten ──────────────────────────────────────────────────

export function useKundenarten(nurAktive = true) {
  return useQuery({
    queryKey: ['kundenarten', nurAktive] as const,
    queryFn: ({ signal }) =>
      api.holen<Kundenartkatalog[]>(
        `/api/kataloge/kundenarten?nur_aktive=${nurAktive}`,
        signal,
      ),
    staleTime: 60 * 60 * 1000,
  })
}

function kundenartenNeuLaden(speicher: ReturnType<typeof useQueryClient>) {
  return speicher.invalidateQueries({ queryKey: ['kundenarten'] })
}

export function useKundenartAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: { name: string }) =>
      api.senden<Kundenartkatalog>('/api/kataloge/kundenarten', daten),
    onSuccess: () => kundenartenNeuLaden(speicher),
  })
}

export function useKundenartAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Partial<Kundenartkatalog>) =>
      api.aendern<Kundenartkatalog>(`/api/kataloge/kundenarten/${id}`, daten),
    onSuccess: () => kundenartenNeuLaden(speicher),
  })
}

export function useKundenartLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/kataloge/kundenarten/${id}`),
    onSuccess: () => kundenartenNeuLaden(speicher),
  })
}

export function useGeraetetypen() {
  return useQuery({
    queryKey: schluessel.geraetetypen,
    queryFn: ({ signal }) => api.holen<Geraetetyp[]>('/api/kataloge/geraetetypen', signal),
    staleTime: 60 * 60 * 1000,
  })
}

export function useBausteine(kategorie?: Bausteinkategorie) {
  return useQuery({
    queryKey: schluessel.bausteine(kategorie),
    queryFn: ({ signal }) =>
      api.holen<Textbaustein[]>(
        kategorie
          ? `/api/kataloge/textbausteine?kategorie=${kategorie}`
          : '/api/kataloge/textbausteine',
        signal,
      ),
    staleTime: 10 * 60 * 1000,
  })
}

export function useBausteinAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: Partial<Textbaustein>) =>
      api.senden<Textbaustein>('/api/kataloge/textbausteine', daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['bausteine'] }),
  })
}

/**
 * Einen vorhandenen Baustein ändern.
 *
 * Die Kategorie gehört nicht dazu: ein Mangeltext, der zu einer Bemerkung
 * wird, taucht in den Vorschlägen an einer ganz anderen Stelle auf, und die
 * bisherigen Verwendungen zählten dann für etwas anderes. Neu anlegen ist
 * dafür der ehrlichere Weg.
 */
export function useBausteinAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Partial<Textbaustein>) =>
      api.aendern<Textbaustein>(`/api/kataloge/textbausteine/${id}`, daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['bausteine'] }),
  })
}

/* Löschen ist beim Textbaustein ungefährlich: er ist eine Schreibhilfe,
   sein Text wird beim Verwenden in den Befund kopiert, kein Bericht
   verweist auf ihn zurück. Stilllegen bleibt daneben – für den Baustein,
   den man vielleicht wieder braucht. */
export function useBausteinLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/kataloge/textbausteine/${id}`),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['bausteine'] }),
  })
}

export type Loeschvorschau = {
  kunde_id: string
  name: string
  spielplaetze: number
  geraete: number
  pruefungen: number
  abgeschlossene_pruefungen: number
  berichtsnummern: string[]
  belege: number
  belegnummern: string[]
  fotos: number
  dokumente: number
  portalzugaenge: number
  ist_beispiel: boolean
}

export function useLoeschvorschau(kundeId: string, aktiv: boolean) {
  return useQuery({
    queryKey: ['loeschvorschau', kundeId] as const,
    queryFn: ({ signal }) =>
      api.holen<Loeschvorschau>(`/api/kunden/${kundeId}/loeschvorschau`, signal),
    enabled: aktiv,
  })
}

export function useKundeLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, bestaetigung }: { id: string; bestaetigung: string }) =>
      api.loeschenMit(`/api/kunden/${id}`, { bestaetigung }),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['kunden'] }),
  })
}

// ── Prüfungen ────────────────────────────────────────────────────

/* Löschen gibt es nur für Entwürfe und laufende Prüfungen; eine
   abgeschlossene weist der Server mit 409 ab. Die Regel steht dort und
   nicht hier – ein Knopf, den man nicht sieht, ist keine Sperre. */
export function usePruefungLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/pruefungen/${id}`),
    onSuccess: () => {
      void speicher.invalidateQueries({ queryKey: ['pruefungen'] })
      void speicher.invalidateQueries({ queryKey: ['aufgaben'] })
    },
  })
}

export type Versandergebnis = { verschickt: boolean; an: string; dateiname: string }

/** Den fertigen Bericht als PDF an den Kunden schicken.
 *
 *  Der Weg über das Kundenportal bleibt daneben bestehen. Eine kleine
 *  Gemeinde, die einmal im Jahr geprüft wird, richtet dafür keinen
 *  Portalzugang ein – die will den Bericht in der Mail. */
export function useBerichtVersenden() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, an }: { id: string; an?: string }) =>
      api.senden<Versandergebnis>(`/api/pruefungen/${id}/versenden`, {
        an: an || null,
      }),
    onSuccess: () => {
      // Der Versand steht danach in der Kontakthistorie des Kunden.
      void speicher.invalidateQueries({ queryKey: ['kundenkontakte'] })
    },
  })
}

export function usePruefungen(zustand?: Pruefstatus, spielplatzId?: string) {
  const teile = new URLSearchParams()
  if (zustand) teile.set('zustand', zustand)
  if (spielplatzId) teile.set('spielplatz_id', spielplatzId)
  const suche = teile.toString()

  return useQuery({
    queryKey: schluessel.pruefungen(suche || undefined),
    queryFn: ({ signal }) =>
      api.holen<Pruefung[]>(`/api/pruefungen${suche ? `?${suche}` : ''}`, signal),
  })
}

export function usePruefungAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: {
      spielplatz_id: string
      pruefart: string
      datum: string
      wetter?: string | null
      temperatur_c?: number | null
      begleitung?: string | null
      /* Prüfgrundlagen. Fehlt ein Feld, gilt die Vorgabe des Servers. */
      grundlage_en1176?: boolean
      grundlage_dguv_202_022?: boolean
      grundlage_dguv_r102_002?: boolean
      grundlage_din18034?: boolean
      grundlage_en16630?: boolean
    }) => api.senden<Pruefung>('/api/pruefungen', daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['pruefungen'] }),
  })
}

/**
 * Prüfgrundlagen einer laufenden Prüfung ändern.
 *
 * Eigener Haken und nicht Teil des Bogens: die Normen stehen auf der
 * Bescheinigung, nicht im Befund. Sie werden im Büro gesetzt und auf dem
 * Platz höchstens einmal berichtigt, wenn dort doch Fitnessgeräte stehen.
 */
export function usePruefungAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Partial<Pruefung>) =>
      api.aendern<Pruefung>(`/api/pruefungen/${id}`, daten),
    onSuccess: (pruefung) => {
      void speicher.invalidateQueries({ queryKey: ['pruefungen'] })
      void speicher.invalidateQueries({ queryKey: schluessel.bogen(pruefung.id) })
    },
  })
}

/**
 * Der Prüfbogen.
 *
 * Nach dem Laden wandert er in IndexedDB. Schlägt das Laden fehl – kein Netz
 * auf dem Platz –, kommt die abgelegte Fassung zum Einsatz. Der Prüfer soll
 * den Bogen auch dann sehen, wenn er ihn im Büro geladen und draußen keinen
 * Empfang hat.
 */
export function usePruefbogen(id: string) {
  return useQuery({
    queryKey: schluessel.bogen(id),
    queryFn: async ({ signal }) => {
      try {
        const bogen = await api.holen<Pruefbogen>(`/api/pruefungen/${id}`, signal)
        await bogenAblegen(bogen)
        return bogen
      } catch (fehler) {
        const abgelegt = await bogenLesen(id)
        if (abgelegt) return abgelegt
        throw fehler
      }
    },
  })
}

export function useFotos(pruefungId: string) {
  return useQuery({
    queryKey: schluessel.fotos(pruefungId),
    queryFn: ({ signal }) =>
      api.holen<Foto[]>(`/api/pruefungen/${pruefungId}/fotos`, signal),
  })
}

// ── Nachprüfung: offene Mängel aus früheren Prüfungen ────────────

export type Altmangel = {
  id: string
  geraet_bezeichnung: string
  kennung: string | null
  geraetenummer: string | null
  stufe: Stufe | null
  mangel: string | null
  normverweis: string | null
  gesperrt: boolean
  mangelstatus: Mangelstatus | null
  /** Erwartet der Prüfer eine Rückmeldung des Kunden zur Behebung? */
  meldung_erwartet: boolean
  frist_am: string | null
  festgestellt_am: string
  berichtsnummer: string | null
  gemeldet_am: string | null
  meldungstext: string | null
}

export function useAltmaengel(pruefungId: string, aktiv: boolean) {
  return useQuery({
    queryKey: ['altmaengel', pruefungId] as const,
    // Nur bei einer Nachprüfung – sonst ist die Liste immer leer und der
    // Aufruf reine Last.
    enabled: aktiv,
    queryFn: ({ signal }) =>
      api.holen<Altmangel[]>(`/api/pruefungen/${pruefungId}/altmaengel`, signal),
  })
}

export function useBehebungBestaetigen(pruefungId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ befundId, bemerkung }: { befundId: string; bemerkung?: string }) =>
      api.senden(`/api/pruefungen/geraetebefunde/${befundId}/behebung/bestaetigen`, {
        nachpruefung_id: pruefungId,
        bemerkung,
      }),
    onSuccess: () => {
      speicher.invalidateQueries({ queryKey: ['altmaengel', pruefungId] })
      speicher.invalidateQueries({ queryKey: schluessel.bogen(pruefungId) })
      speicher.invalidateQueries({ queryKey: ['spielplaetze'] })
    },
  })
}

export function useBehebungAblehnen(pruefungId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ befundId, grund }: { befundId: string; grund: string }) =>
      api.senden(`/api/pruefungen/geraetebefunde/${befundId}/behebung/ablehnen`, {
        nachpruefung_id: pruefungId,
        grund,
      }),
    onSuccess: () => {
      speicher.invalidateQueries({ queryKey: ['altmaengel', pruefungId] })
      speicher.invalidateQueries({ queryKey: ['spielplaetze'] })
    },
  })
}

// ── Schnellsuche ─────────────────────────────────────────────────

export type Treffer = {
  art: 'kunde' | 'spielplatz' | 'geraet'
  id: string
  titel: string
  unterzeile: string | null
  kunde_id: string | null
  /** Nur bei Geräten: der Sprung geht auf den Platz, nicht aufs Gerät. */
  spielplatz_id: string | null
  offene_maengel: number
}

export function useSuche(begriff: string) {
  const sauber = begriff.trim()
  return useQuery({
    queryKey: ['suche', sauber] as const,
    // Ab zwei Zeichen, wie der Server. Darunter gar nicht erst fragen.
    enabled: sauber.length >= 2,
    // Kurz frisch halten: wer tippt, löscht und neu tippt, soll nicht warten.
    staleTime: 30_000,
    queryFn: ({ signal }) =>
      api.holen<Treffer[]>(`/api/suche?q=${encodeURIComponent(sauber)}`, signal),
  })
}
